import React, { useEffect, useState } from 'react';
import { AppLogo } from '@/components/shared/display/Logo';
import { FormattedText } from '@/components/uiKit/FormattedText';
import { analytics } from '@/analytics';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { locations } from '@/locations';
import { diagnosticApi } from '@/store/api/diagnosticApi';
import { userApi } from '@/store/api/userApi';
import { useAuth } from '@/providers/AuthProvider';
import { useDispatch } from 'react-redux';
import { useLazyGetStatusQuery, useSendCodeMutation } from '@/store/api/authApi';
import { TOAST_STATUS } from '@/const';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/shared/use-toast';

export const MethodVerificationPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { signOut } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sendVerificationCode, { isLoading: isSendingCode }] = useSendCodeMutation();
  const [fetchAuthStatus, { data: authStatus }] = useLazyGetStatusQuery();
  const [selectedMethod, setSelectedMethod] = useState<string>('email');

  useEffect(() => {
    analytics.page({
      name: 'Code Verification',
      title: 'DataMind Dashboard 2.0 - Code Verification',
    });
  }, []);

  const onMethodChange = (value: string) => {
    setSelectedMethod(value);
    setSearchParams({ method: value });
  };

  const onBack = async () => {
    await signOut();
    dispatch(diagnosticApi.util.resetApiState());
    dispatch(userApi.util.resetApiState());
    navigate(locations.public.login);
  };

  const onNext = async () => {
    await sendVerificationCode({
      method: selectedMethod,
    })
      .unwrap()
      .then(() => {
        toast({
          title: 'Your OTP code has been sent!',
          description: 'You\'re just one step away from completing the process.',
        });
        navigate(`${locations.public.secondFactorVerification}?method=${searchParams.get('method')}`);
      })
      .catch((error) => {
        Sentry.captureException(error);
        toast({
          variant: 'destructive',
          title: 'OTP Sending Failed',
          description:
            'An error occurred while sending the verification code. Please try again or check your network connection.',
        });
      });
  };

  useEffect(() => {
    fetchAuthStatus({});
    setSearchParams({ method: 'email' });
  }, []);

  return (
    <div className="flex flex-col h-full justify-center px-4 xl:px-0">
      <div className="max-w-[26.25rem] mx-auto w-full bg-white xl:bg-transparent rounded-lg xl:rounded-none px-6 xl:px-0 py-12 xl:py-0">
        {/* Mobile Logo */}
        <div className="flex xl:hidden w-full justify-center mb-8">
          <AppLogo size="184px" />
        </div>

        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-center xl:text-left w-full xl:w-auto">
            <span className="block xl:hidden">
              <FormattedText label="auth.login.headingMobile" />
            </span>
            <span className="hidden xl:block">
              <FormattedText label="auth.login.heading" />
            </span>
          </h1>

          {/* Desktop Logo */}
          <div className="hidden xl:block ml-[-4px] mt-[-2px]">
            <AppLogo size="164px" />
          </div>
        </div>

        <div className="pb-10 pt-2 xl:pt-6 text-center xl:text-left">
          <FormattedText label="auth.2fa.method.description" />
        </div>

        <div className="flex w-full justify-center pb-8">
          {authStatus ? (
            <RadioGroup value={selectedMethod} onValueChange={onMethodChange}>
              <div className="flex gap-12">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem 
                    value="email" 
                    id="email" 
                    disabled={!authStatus.methods.email}
                  />
                  <Label 
                    htmlFor="email" 
                    className={!authStatus.methods.email ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  >
                    Email
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem 
                    value="sms" 
                    id="sms" 
                    disabled={!authStatus.methods.sms}
                  />
                  <Label 
                    htmlFor="sms" 
                    className={!authStatus.methods.sms ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  >
                    SMS
                  </Label>
                </div>
              </div>
            </RadioGroup>
          ) : null}
        </div>

        <div className="flex flex-col-reverse md:flex-row mt-8 justify-between gap-4">
          <div className="w-full text-center">
            <Button variant="ghost" onClick={onBack} className="text-blue-600 hover:text-blue-700">
              <FormattedText label="auth.2fa.method.back" />
            </Button>
          </div>

          <div className="w-full text-center mb-5 md:mb-0">
            <Button
              className="w-4/5 md:w-full mx-auto"
              onClick={onNext}
              disabled={isSendingCode}
            >
              {isSendingCode ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </span>
              ) : (
                <FormattedText label="auth.2fa.method.next" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};